import { useEffect, useState } from 'react';
import { UploadWidgetFailedFile } from '@bytescale/upload-widget';

import { ImageForDropzone, MultiImageFormData } from 'app/shared/typings';
import { RoutableModalFormProps } from 'app/shared/typings/routableModal';

import { calculateImgIXParams, getImagesToSave } from './utils';

interface Props extends RoutableModalFormProps<MultiImageFormData<any>> {
  imageFieldName: string;
  setIsSubmitButtonDisabled: (disabled: boolean) => void;
  setIsSubmitting: (set: boolean) => void;
}

type ImgIXParamsMap = Record<string, string>;

export const useMultiImageUpload = ({
  formikProps,
  imageFieldName,
  setIsSubmitButtonDisabled,
  setFormSubmitAction,
  setDisplayConfirmation,
  setIsSubmitting,
}: Props) => {
  const {
    values,
    errors,
    setFieldValue,
    dirty,
    isSubmitting,
    submitForm,
    setFieldError,
  } = formikProps;
  const [failedFiles, setFailedFiles] = useState<UploadWidgetFailedFile[]>([]);

  const [imagesForAllRatios, setImagesForAllRatios] = useState<
    ImageForDropzone[]
  >([]);
  const [imgIXParamsMap, setImgIXParamsMap] = useState<ImgIXParamsMap>({});

  // Returns a new imgIXParamsMap object with only the entries that have a corresponding image in imagesForAllRatios
  function filteredImgIXParamsMap() {
    return Object.keys(imgIXParamsMap).reduce(
      (acc: ImgIXParamsMap, key: string) => {
        if (imagesForAllRatios.find((image) => image.fileUrl === key)) {
          acc[key] = imgIXParamsMap[key];
        }
        return acc;
      },
      {}
    );
  }

  useEffect(() => {
    if (imagesForAllRatios.length < Object.keys(imgIXParamsMap).length) {
      setImgIXParamsMap(filteredImgIXParamsMap());
    } else {
      calculateImgIXParams({ setImgIXParamsMap, imagesForAllRatios });
    }
  }, [imagesForAllRatios]);

  useEffect(() => {
    setFieldError(imageFieldName, failedFiles[0]?.error?.message);
    setFieldValue(
      imageFieldName,
      getImagesToSave({ imagesForAllRatios, imgIXParamsMap })
    );
  }, [imgIXParamsMap]);

  useEffect(() => {
    setFormSubmitAction && setFormSubmitAction(() => submitForm);

    const numImages = imagesForAllRatios.length;
    const numImgIXParamKeys = Object.keys(imgIXParamsMap).length;
    const numImagesFieldValues = values[imageFieldName].length;
    const uploadsValid =
      failedFiles.length === 0 &&
      numImages > 0 &&
      numImgIXParamKeys == numImages &&
      numImagesFieldValues == numImages;
    setIsSubmitButtonDisabled(!uploadsValid || !!errors[imageFieldName]);

    setDisplayConfirmation &&
      setDisplayConfirmation(dirty || numImagesFieldValues > 0);
    setIsSubmitting(isSubmitting);
  }, [
    failedFiles,
    errors[imageFieldName],
    values[imageFieldName],
    isSubmitting,
    submitForm,
    dirty,
    setFormSubmitAction,
    setDisplayConfirmation,
    setIsSubmitting,
  ]);

  return {
    imagesForAllRatios,
    setImagesForAllRatios,
    failedFiles,
    setFailedFiles,
  };
};

export { compressImageFile } from './utils';
